<template>
  <PageProfileFeedLayout>
    <div class="space-y-4">
      <ClientOnly>
        <PostListVirtual
          :fetchNext="fetchNext"
          :noPostsIconLink="getAbsoluteAssetLink('nami/stickers/sad.png')"
          noPostsMessage="This organization has no posts."
          ref="feedList"
        />
      </ClientOnly>
    </div>
    <template #extra>
      <div class="space-y-4">
        <div class="space-y-2">
          <h2 class="text-xl font-bold mb-4">Owner</h2>
          <UserCard v-if="data" :user="data.leader" />
          <NamiLoading v-else size="small" />
        </div>
        <div class="space-y-2">
          <h2 class="text-xl font-bold mb-4">Members</h2>
          <UserCard
            v-for="member in data?.members"
            :user="member"
            :key="member.id"
          />
          <NamiLoading v-if="memberPending" size="small" />
          <div v-else-if="data?.members.length === 0" class="italic">
            No members
          </div>
        </div>
      </div>
    </template>
  </PageProfileFeedLayout>
</template>

<script lang="ts" setup>
import {
  getMultipleRelationships,
  isUser,
  type OrganizationEntity,
  Post,
  type PostListParams,
} from "~/src/api";
import { useQuery } from "@tanstack/vue-query";

const { org } = defineProps<{
  org: OrganizationEntity;
}>();

const nuxtApp = useNuxtApp();
const authStore = nuxtApp.$auth();

async function fetchNext(params: PostListParams) {
  const token = (await authStore?.getToken()) ?? undefined;
  return await Post.getPosts(
    {
      organizationId: org.id,
      order: {
        publishedAt: "desc",
      },
      includeReposts: true,
      ...params,
    },
    token,
  );
}

const {
  data,
  isPending: memberPending,
  suspense,
} = useQuery({
  queryKey: ["org", "members", computed(() => org.id)],
  queryFn: async () => {
    const owner = org.relationships.find(isUser)?.id;
    const members = getMultipleRelationships(org, "member").map(
      (rel) => rel.id,
    );

    const fetched = await getUsersFromIds(
      members.concat([owner ?? ""]).filter(onlyTruthys),
      ["profile_frame", "badge"],
      await authStore?.getToken(),
    );

    return {
      members: fetched.data.filter((user) => user.id !== owner),
      leader: fetched.data.find((user) => user.id === owner)!,
    };
  },
  staleTime: useCacheTimings().collection.user,
});

onServerPrefetch(async () => {
  await suspense();
});

const feedList = ref();

onActivated(() => {
  feedList.value?.checkForNewPosts();
});
</script>
